import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TracerService } from '../../../services/tracer.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { GithubStatus, GithubRepoOption } from '../../../models/ai.model';

// Admin-only "GitHub Issues" settings tab. Connects this Tracer
// instance to ONE GitHub repo the light way: an admin pastes a GitHub
// access token (a fine-grained PAT scoped to the target repo,
// Issues:write), the backend validates it against the GitHub API and
// stores it server-side, then the admin picks the repo from the list
// of repositories that token can push to. No OAuth App required. The
// legacy TRACER_GITHUB_TOKEN env still works as a fallback.
@Component({
  selector: 'app-github-settings',
  imports: [FormsModule],
  templateUrl: './github-settings.html',
})
export class GithubSettings implements OnInit {
  agRealm: string | null = null;

  github: GithubStatus | null = null;
  repos: GithubRepoOption[] = [];
  repoFilter = '';
  selectedRepo = '';

  // Write-only: bound to the password field, cleared after save, never
  // populated from the server (the token is never sent back).
  tokenInput = '';
  showTokenForm = false;

  loadingRepos = false;
  tokenSaving = false;
  githubSaving = false;
  githubError: string | null = null;
  githubSaved = false;

  constructor(
    private tracer: TracerService,
    private ls: LocalStorageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.agRealm = this.ls.getItem('agRealm');
    if (this.agRealm !== 'admins') {
      this.router.navigateByUrl('/');
      return;
    }
    this.loadGithub();
  }

  /** GitHub's fine-grained PAT creation page (recommended — least
   *  privilege). The repo can't be deep-linked here, so we show exact
   *  inline instructions referencing the repo the admin typed. */
  readonly fineGrainedTokenUrl = 'https://github.com/settings/personal-access-tokens/new';

  /** Classic-token page DOES accept a prefilled description + scope,
   *  so this one is effectively one-click. */
  get classicTokenUrl(): string {
    const repo = this.selectedRepo.trim();
    const desc = 'AGW Tracer' + (repo ? ' – ' + repo : '');
    return `https://github.com/settings/tokens/new?description=${encodeURIComponent(
      desc
    )}&scopes=repo`;
  }

  get connectedViaToken(): boolean {
    return this.github?.auth_method === 'token';
  }

  get filteredRepos(): GithubRepoOption[] {
    const q = this.repoFilter.trim().toLowerCase();
    if (!q) return this.repos;
    return this.repos.filter(r => r.full_name.toLowerCase().includes(q));
  }

  loadGithub(): void {
    this.tracer.getGithubStatus().subscribe({
      next: res => {
        this.github = res.github;
        this.selectedRepo = res.github.repo || '';
        if (res.github.auth_method === 'token') {
          this.loadRepos();
        }
      },
      error: () => {
        this.github = null;
      },
    });
  }

  loadRepos(): void {
    this.loadingRepos = true;
    this.tracer.getGithubRepos().subscribe({
      next: res => {
        this.loadingRepos = false;
        this.repos = res.repos || [];
      },
      error: err => {
        this.loadingRepos = false;
        this.githubError = err?.error?.error || 'Failed to list repositories.';
      },
    });
  }

  saveToken(): void {
    const token = this.tokenInput.trim();
    if (!token) return;
    this.tokenSaving = true;
    this.githubError = null;
    this.tracer.setGithubToken(token).subscribe({
      next: res => {
        this.tokenSaving = false;
        this.tokenInput = '';
        this.showTokenForm = false;
        this.github = res.github;
        this.selectedRepo = res.github.repo || '';
        if (res.github.auth_method === 'token') this.loadRepos();
      },
      error: err => {
        this.tokenSaving = false;
        this.githubError = err?.error?.error || 'GitHub rejected that token.';
      },
    });
  }

  saveRepo(repo: string): void {
    this.githubSaving = true;
    this.githubError = null;
    this.githubSaved = false;
    this.tracer.setGithubRepo(repo.trim()).subscribe({
      next: res => {
        this.githubSaving = false;
        this.githubSaved = true;
        this.github = res.github;
        this.selectedRepo = res.github.repo || '';
      },
      error: err => {
        this.githubSaving = false;
        this.githubError = err?.error?.error || 'Failed to save the repository.';
      },
    });
  }

  disconnect(): void {
    this.githubSaving = true;
    this.githubError = null;
    this.tracer.disconnectGithub().subscribe({
      next: res => {
        this.githubSaving = false;
        this.github = res.github;
        this.repos = [];
        this.selectedRepo = '';
        this.showTokenForm = false;
      },
      error: err => {
        this.githubSaving = false;
        this.githubError = err?.error?.error || 'Failed to disconnect.';
      },
    });
  }
}
